import React, { useState, useEffect, useCallback, useRef } from 'react'
import ReactMapboxGl, { ZoomControl, MapContext } from 'react-mapbox-gl'
import { useMutation } from '@apollo/react-hooks'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import RadiusMode from './RadiusMode'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import ObstacleDialog from './ObstacleDialog'
import AttackDialog from './AttackDialog'
import BackgroundMap from './BackgroundMap'
import {
  saveObstacleData,
  saveAttackData,
  selectObstacle,
  selectAttack,
  deleteReduxObstacleByGuid,
  deleteReduxAttackByGuid,
  updateObstacleDataByGuid,
  updateAttackDataByGuid,
} from '../../actions'
import {
  SAVE_OBSTACLE_BY_GUID,
  SAVE_OBSTACLE,
  DELETE_OBSTACLE_BY_GUID,
} from '../../queries/obstacle'
import {
  SAVE_ATTACK_BY_GUID,
  SAVE_ATTACK,
  DELETE_ATTACK_BY_GUID,
} from '../../queries/attack'
import Layers from '../Layers'
import { styles } from './styles'
import uuid from 'uuid'
import { mapStyle } from '../../../../lib/util'
import turfdistance from '@turf/line-distance'
import * as turf from '@turf/turf'
// import Tools from '../Tools'
import Report from '../Report'

const MapBox = ReactMapboxGl({
  accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
  preserveDrawingBuffer: true,
})

const draw = new MapboxDraw({
  modes: {
    ...MapboxDraw.modes,
    draw_circle: RadiusMode,
  },
  userProperties: true,
  displayControlsDefault: false,
  controls: {
    line_string: true,
    polygon: true,
    trash: true,
  },
  styles: styles,
})
draw.modes.DRAW_CIRCLE = 'draw_circle'

export default function Map() {
  const [obstacleDialogOpen, setObstacleDialogOpen] = useState(false)
  const [attackDialogOpen, setAttackDialogOpen] = useState(false)
  const {
    projects,
    selectedProjectId,
    selectedScenarioId,
    mapChoice,
    remoteStorage,
    obstacles,
    attacks,
  } = useSelector(state => state)
  const dispatch = useDispatch()
  const [obstacle, setObstacle] = useState({})
  const [attack, setAttack] = useState({})
  const [geojson, setGeojson] = useState(null)
  const [center, setCenter] = useState(null)
  const [zoom, setZoom] = useState(15)
  const [saveObstacle] = useMutation(SAVE_OBSTACLE)
  const [saveObstacleByGuid] = useMutation(SAVE_OBSTACLE_BY_GUID)
  const [saveAttackByGuid] = useMutation(SAVE_ATTACK_BY_GUID)
  const [deleteObstacleByGuid] = useMutation(DELETE_OBSTACLE_BY_GUID)
  const [deleteAttackByGuid] = useMutation(DELETE_ATTACK_BY_GUID)
  const [saveAttack] = useMutation(SAVE_ATTACK)

  useEffect(() => {
    if (projects.length > 0) {
      let project = projects.filter(p => p.id === selectedProjectId)[0]
      let centerObject
      if (project.geojson) {
        setGeojson(JSON.parse(project.geojson))
        centerObject = turf.centerOfMass(JSON.parse(project.geojson)).geometry
          .coordinates
        setCenter(centerObject)
      } else {
        setGeojson(null)
        if (project.center !== null) {
          centerObject = JSON.parse(project.center)
          setCenter([centerObject.lon, centerObject.lat])
        } else {
          setCenter(null)
        }
      }
    }
  }, [selectedProjectId, projects])

  const [_, forceUpdate] = useState(0)
  const mapRef = useRef()
  const map = mapRef.current
  const handleMapLoaded = useCallback(
    map => {
      mapRef.current = map

      mapRef.current.on('draw.create', function (e) {
        console.log('in create', e.features)
        let geoJsonFeature
        if (e.features[0].geometry.type === 'Polygon') {
          obstacle.id = null
          obstacle.geom_guid = e.features[0].id
          geoJsonFeature = JSON.stringify(e.features[0])
          obstacle.geojson = geoJsonFeature
          setObstacle(obstacle)
          obstacleDialogOpenHandler(true)
        } else if (e.features[0].geometry.type === 'LineString') {
          console.log('attack length=', turfdistance(e.features[0]) * 1000)
          attack.id = null
          attack.geom_guid = e.features[0].id
          geoJsonFeature = JSON.stringify(e.features[0])
          attack.geojson = geoJsonFeature
          setAttack(attack)
          attackDialogOpenHandler(true)
        }
      })

      mapRef.current.on('draw.update', async function (e) {
        console.log('in update', e.features)

        let payload
        if (e.features[0].geometry.type === 'Polygon') {
          if (remoteStorage.obstacles) {
            payload = {
              geojson: JSON.stringify(e.features[0]),
              geom_guid: e.features[0].id,
            }
            await saveObstacleByGuid({
              variables: { input: payload },
            })
          } else {
            dispatch(
              updateObstacleDataByGuid(e.features[0].id, {
                geojson: JSON.stringify(e.features[0]),
              })
            )
          }
        } else if (e.features[0].geometry.type === 'LineString') {
          console.log('attack length=', turfdistance(e.features[0]) * 1000)
          if (remoteStorage.attacks) {
            payload = {
              geojson: JSON.stringify(e.features[0]),
              geom_guid: e.features[0].id,
            }
            await saveAttackByGuid({
              variables: { input: payload },
            })
          } else {
            dispatch(
              updateAttackDataByGuid(e.features[0].id, {
                geojson: JSON.stringify(e.features[0]),
              })
            )
          }
        }
      })

      mapRef.current.on('draw.delete', async function (e) {
        console.log('in delete', e.features)

        if (e.features[0].geometry.type === 'Polygon') {
          if (remoteStorage.obstacles) {
            await deleteObstacleByGuid({
              variables: { id: e.features[0].id },
            })
          }
          dispatch(deleteReduxObstacleByGuid(e.features[0].id))
        } else if (e.features[0].geometry.type === 'LineString') {
          if (remoteStorage.attacks) {
            await deleteAttackByGuid({
              variables: { id: e.features[0].id },
            })
          }
          dispatch(deleteReduxAttackByGuid(e.features[0].id))
        } else if (
          e.features[0].geometry.type === 'Point' ||
          e.features[0].geometry.type === 'MultiLineString'
        ) {
          if (remoteStorage.attacks) {
            await deleteAttackByGuid({
              variables: { id: e.features[0].id },
            })
          }
          dispatch(deleteReduxAttackByGuid(e.features[0].id))
        }
      })

      mapRef.current.on('draw.selectionchange', async function (e) {
        console.log('in selectionchange', e.features)

        if (e.features.length) {
          if (e.features[0].geometry.type === 'Polygon') {
            dispatch(selectObstacle(e.features[0].id))
          } else if (
            e.features[0].geometry.type === 'LineString' ||
            e.features[0].geometry.type === 'MultiLineString'
          ) {
            dispatch(selectAttack(e.features[0].id))
          }
        } else {
          dispatch(selectObstacle(null))
          dispatch(selectAttack(null))
        }
      })

      mapRef.current.on('zoomend', function (e) {
        if (e.originalEvent) {
          setZoom(mapRef.current.getZoom())
        }
      })

      mapRef.current.addControl(draw)
      // eslint-disable-next-line no-undef
      for (let el of document.querySelectorAll(
        '.mapboxgl-ctrl-group.mapboxgl-ctrl'
      ))
        el.style.visibility = 'hidden'

      forceUpdate(_ + 1)
    },
    [map]
  )

  const loadFromStateToMap = draw => {
    try {
      obstacles
        .filter(
          obstacle =>
            String(obstacle.scenario_id) === String(selectedScenarioId)
        )
        .map(obstacle => {
          draw.add(JSON.parse(obstacle.geojson))
        })
      attacks
        .filter(
          attack =>
            String(attack.scenario_id) === String(selectedScenarioId) &&
            attack.active
        )
        .map(attack => {
          const attackFeature = JSON.parse(attack.geojson)
          attackFeature.id = attack.geom_guid
          attackFeature.properties = {
            ...(attackFeature.properties || {}),
            line_color: attack.original_id
              ? attack.line_color ||
                attackFeature.properties?.line_color ||
                '#ff0000'
              : '#3bb2d0',
          }
          draw.add(attackFeature)
        })
    } catch (e) {
      console.error('loadFromStateToMap ', e)
    }
  }

  const syncAttackColorToDraw = attackItem => {
    try {
      const attackFeature = JSON.parse(attackItem.geojson || '{}')
      attackFeature.id = attackItem.geom_guid
      attackFeature.properties = {
        ...(attackFeature.properties || {}),
        line_color: attackItem.original_id
          ? attackItem.line_color ||
            attackFeature.properties?.line_color ||
            '#ff0000'
          : '#3bb2d0',
      }

      if (draw.get(attackItem.geom_guid)) {
        draw.delete(attackItem.geom_guid)
      }
      draw.add(attackFeature)
    } catch (e) {
      console.error('syncAttackColorToDraw ', e)
    }
  }

  useEffect(() => {
    if (map) {
      draw.deleteAll()
      if (selectedScenarioId) {
        if (!remoteStorage.obstacles && !remoteStorage.attacks) {
          loadFromStateToMap(draw)
        }
        // eslint-disable-next-line no-undef
        for (let el of document.querySelectorAll(
          '.mapboxgl-ctrl-group.mapboxgl-ctrl'
        ))
          el.style.visibility = 'visible'
      } else {
        // eslint-disable-next-line no-undef
        for (let el of document.querySelectorAll(
          '.mapboxgl-ctrl-group.mapboxgl-ctrl'
        ))
          el.style.visibility = 'hidden'
      }
    }
  }, [
    selectedScenarioId,
    selectedProjectId,
    map,
    remoteStorage.obstacles,
    remoteStorage.attacks,
    obstacles,
    attacks,
  ])

  useEffect(() => {
    if (!map || !selectedScenarioId) return

    attacks
      .filter(
        attack => String(attack.scenario_id) === String(selectedScenarioId)
      )
      .forEach(syncAttackColorToDraw)
  }, [map, selectedScenarioId, attacks])

  const saveObstacleToDatabase = async input => {
    console.log(input)
    const obstacleObj = await saveObstacle({ variables: { input: input } })
    return obstacleObj.data.createOrUpdateObstacle.id
  }

  const saveAttackToDatabase = async input => {
    console.log(input)
    const attackId = await saveAttack({ variables: { input: input } })
    return attackId.data.createOrUpdateAttack.id
  }

  const attackDialogOpenHandler = async (
    value,
    name = '',
    speed = '',
    length = '',
    vehicle = ''
  ) => {
    const lineColor = '#3bb2d0'
    setAttackDialogOpen(value)
    if (name !== '' && speed !== '' && length !== '' && vehicle !== '') {
      let attackGeojson = JSON.parse(attack.geojson)
      attackGeojson.properties = {
        ...(attackGeojson.properties || {}),
        line_color: lineColor,
      }
      attack.name = name
      attack.start_speed = Number(speed)
      attack.path_length = Number(length)
      attack.vehicle = vehicle
      attack.line_color = lineColor
      attack.geojson = JSON.stringify(attackGeojson)
      draw.setFeatureProperty(attack.geom_guid, 'line_color', lineColor)
      attack.project_id = selectedProjectId
      attack.scenario_id = selectedScenarioId
      const project = projects.filter(p => p.id === selectedProjectId)[0]
      attack.units = { ...project.units }
      if (remoteStorage.attacks) {
        attack.id = await saveAttackToDatabase({ ...attack })
      } else {
        attack.id = uuid()
      }
      dispatch(saveAttackData(attack))
      dispatch(selectAttack(attack.geom_guid))
      setAttack({})
    } else if (!value && name === '') {
      const featureId = attack.geom_guid
      draw.delete(featureId)
      setAttack({})
      dispatch(selectAttack(null))
    }
  }

  const obstacleDialogOpenHandler = async (value, name = '') => {
    setObstacleDialogOpen(value)
    if (name !== '') {
      obstacle.name = name
      obstacle.project_id = selectedProjectId
      obstacle.scenario_id = selectedScenarioId
      if (remoteStorage.obstacles) {
        obstacle.id = await saveObstacleToDatabase({ ...obstacle })
      } else {
        obstacle.id = uuid()
      }
      dispatch(saveObstacleData(obstacle))
      dispatch(selectObstacle(obstacle.geom_guid))
      setObstacle({})
    } else if (!value && name === '') {
      const featureId = obstacle.geom_guid
      draw.delete(featureId)
      setObstacle({})
      dispatch(selectObstacle(null))
    }
  }

  if (center === null) return <div />

  return (
    <MapBox
      // eslint-disable-next-line
      style={mapStyle(mapChoice)}
      containerStyle={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
      }}
      center={center}
      zoom={[zoom]}
      onStyleLoad={handleMapLoaded}
    >
      <MapContext.Consumer>
        {map => {
          return (
            <div
              style={{
                position: 'absolute',
                right: 10,
                top: 300,
              }}
            >
              <Report map={map} />
            </div>
          )
        }}
      </MapContext.Consumer>
      {/*<Tools saveObstacleToDatabase={saveObstacleToDatabase} draw={draw} />*/}
      <div style={{ position: 'absolute', right: 10, top: 60 }}>
        <ZoomControl />
      </div>
      <div
        style={{
          position: 'absolute',
          right: 20,
          top: 240,
          width: '25px',
          height: '29px',
          backgroundColor: 'white',
          display: 'block',
        }}
      >
        <BackgroundMap />
      </div>
      <ObstacleDialog
        open={obstacleDialogOpen}
        onChange={obstacleDialogOpenHandler}
      />
      <AttackDialog
        open={attackDialogOpen}
        onChange={attackDialogOpenHandler}
      />
      <Layers geojson={geojson} map={map} draw={draw} />
    </MapBox>
  )
}
